import { useState, useEffect } from "react";
import { Loader2, CheckCircle2, AlertCircle, Video } from "lucide-react";
import { getVideoStatus, resolveImageUrl } from "@/lib/api";

/**
 * Component that displays video processing status and polls for updates.
 * When processing completes, it renders the actual video player.
 */
export default function VideoProcessingIndicator({ videoId, videoUrl, onProcessingComplete }) {
  const [status, setStatus] = useState("processing");
  const [statusMessage, setStatusMessage] = useState("Processing video...");
  const [finalUrl, setFinalUrl] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!videoId) {
      // No video ID means it's already processed (legacy video)
      setStatus("completed");
      setFinalUrl(videoUrl);
      return;
    }

    let isMounted = true;
    let pollInterval;

    const checkStatus = async () => {
      try {
        const res = await getVideoStatus(videoId);
        if (!isMounted) return;

        const data = res.data;
        setStatus(data.status);
        setStatusMessage(data.progress_message || getStatusMessage(data.status));

        if (data.status === "completed") {
          setFinalUrl(data.url);
          setThumbnail(data.thumbnail);
          if (onProcessingComplete) {
            onProcessingComplete(data);
          }
          // Stop polling
          if (pollInterval) clearInterval(pollInterval);
        } else if (data.status === "error") {
          setError(data.error_message || "Video processing failed");
          if (pollInterval) clearInterval(pollInterval);
        }
      } catch (err) {
        console.error("Error checking video status:", err);
        // Don't stop polling on error, might be temporary
      }
    };

    // Initial check
    checkStatus();

    // Poll every 2 seconds while processing
    pollInterval = setInterval(checkStatus, 2000);

    return () => {
      isMounted = false;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [videoId, videoUrl, onProcessingComplete]);

  const getStatusMessage = (status) => {
    switch (status) {
      case "queued":
        return "Video upload complete, starting processing...";
      case "transcoding":
        return "Converting video for web playback...";
      case "generating_thumbnail":
        return "Almost done, creating thumbnail...";
      case "completed":
        return "Video ready!";
      case "error":
        return "Processing failed";
      default:
        return "Processing video...";
    }
  };

  // If completed, render the video player
  if (status === "completed" && (finalUrl || videoUrl)) {
    const videoSrc = resolveImageUrl(finalUrl || videoUrl);
    return (
      <div className="relative">
        <video
          key={videoSrc}
          src={videoSrc}
          className="w-full rounded-md border border-[#E5E0D5] max-h-96 bg-black"
          controls
          preload="metadata"
          playsInline
          poster={thumbnail ? resolveImageUrl(thumbnail) : undefined}
        />
      </div>
    );
  }

  // If error, show error state
  if (status === "error") {
    return (
      <div className="w-full h-48 rounded-md border border-red-200 bg-red-50 flex flex-col items-center justify-center text-red-600">
        <AlertCircle className="w-12 h-12 mb-2 opacity-60" />
        <p className="text-sm font-medium font-['Nunito']">Video processing failed</p>
        <p className="text-xs opacity-75 font-['Nunito']">{error || "Please try uploading again"}</p>
      </div>
    );
  }

  // Show processing state
  return (
    <div 
      className="w-full h-48 rounded-md border border-[#E5E0D5] bg-gradient-to-br from-[#F9F7F2] to-[#F0EBE0] flex flex-col items-center justify-center"
      data-testid="video-processing-indicator"
    >
      <div className="relative">
        <Video className="w-12 h-12 text-[var(--liturgical-primary)] opacity-40" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[var(--liturgical-primary)] animate-spin" />
        </div>
      </div>
      <p className="text-sm font-medium font-['Nunito'] text-[#2D2A26] mt-3">
        {statusMessage}
      </p>
      <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">
        This should only take a moment
      </p>
      {/* Progress dots animation */}
      <div className="flex gap-1 mt-2">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--liturgical-primary)] animate-pulse" style={{ animationDelay: "0ms" }} />
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--liturgical-primary)] animate-pulse" style={{ animationDelay: "150ms" }} />
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--liturgical-primary)] animate-pulse" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  );
}
